/**
 * iter845 — POS printing hook. Encapsulates the four printer-routing
 * helpers previously inlined in CashierPOSPage.jsx:
 *   • buildPrintPayload      — assemble the bridge/eposDirect payload
 *   • openOrderInPrintDialog — hidden-iframe OS print-dialog fallback
 *   • printOrderViaBridge    — receipt / kitchen / bar dispatch with
 *                              ePOS-Direct → Cloud Relay → Bridge →
 *                              OS-dialog cascade
 *   • printPrepTickets       — fan-out to every kitchen+bar printer
 *                              configured for this branch
 *
 * Pure lift-and-shift — zero behaviour changes. All decision paths
 * (training mode, iPad, Samsung Tab, silent/auto/dialog, cloud relay
 * 409 handling, ePOS cert-not-accepted messaging) preserved identically.
 */
import { useCallback } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  isBridgeAlive, pickPrinter, printReceipt as bridgePrint,
  printViaCloud, isLikelyIOS, isLikelyMobileCashier,
} from '@/lib/printBridge';
import { filterOrderForPrinter, buildPrintPayloadHelper } from '@/lib/posPrint';
import { tailorReceiptLines } from '@/lib/tailorReceipt';
import { printViaEposDirect } from '@/lib/eposPrint';
import { isCapacitorNative, isAndroid } from '@/lib/platformDetect';

export function usePosPrinting({
  printers,
  user,
  branchTax,
  branchInfo,
  branding,
  categories,
  isTrainingActive,
}) {
  // Build the bridge payload from an order + current cashier context.
  const buildPrintPayload = useCallback(
    (order, opts = {}) =>
      buildPrintPayloadHelper(order, { ...opts, branch: branchInfo, branding }, user, branchTax),
    [branchInfo, branding, user, branchTax],
  );

  // iter611 — Open the OS/browser print dialog for an order. Used as a
  // fallback when the bridge is unreachable AND as the primary path
  // when the printer is in `print_mode: 'dialog'`.
  // iter — The receipt role now renders the COMPLETE Simplified Tax Invoice
  // (item amounts, Subtotal, VAT, Total, payment + ZATCA QR), reusing the
  // existing payload data and the existing buildZatcaQrPayload() TLV builder.
  // Previously this template only listed "qty × name" with a blank amount
  // (it read it.total, which the payload never sets) and no totals/QR, so a
  // tablet falling back to the OS print service printed company + item names
  // only. Async because QR rendering (qrcode lib) is promise-based.
  const openOrderInPrintDialog = useCallback(async (order, role) => {
    const content = buildPrintPayload(order, { role });
    const isReceipt = role === 'receipt';
    const title = content?.title || (isReceipt ? 'Simplified Tax Invoice' : role === 'bar' ? 'BAR' : 'KITCHEN');
    const cur = content?.currency || '';
    const esc = (s) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const money = (n) => `${esc(cur)}${Number(n || 0).toFixed(2)}`;

    // Item rows: qty × name + the line AMOUNT (payload carries `subtotal`,
    // never `total`). Modifiers listed under the line.
    const rows = (content?.items || []).map((it) => {
      const mods = (it.modifiers || []).length
        ? `<div style="font-size:10px;color:#555;padding-left:10px">${it.modifiers.map((m) => `+ ${esc(m.name || m)}`).join('<br/>')}</div>`
        : '';
      return `<div style="display:flex;justify-content:space-between;gap:8px"><span>${esc(it.qty)} × ${esc(it.name)}</span><span>${money(it.subtotal)}</span></div>${mods}`;
    }).join('');

    // iter917 — Tailor MEASUREMENTS / MODIFICATION section (bilingual).
    const tailor = tailorReceiptLines(content?.metadata);
    const tailorHtml = tailor
      ? `<div style="text-align:center;font-weight:bold">${esc(tailor.title)}</div>${tailor.rows.map((r) =>
          `<div style="display:flex;justify-content:space-between;gap:8px"><span>${esc(r.label)}</span><span>${esc(r.value)}</span></div>`
        ).join('')}<hr/>`
      : '';

    // Totals + payment block (receipt only), reusing the existing amounts.
    let totalsHtml = '';
    let metaHtml = '';
    let sellerHtml = '';
    if (isReceipt) {
      const vatPct = content?.tax_pct != null ? ` (${esc(content.tax_pct)}%)` : '';
      const payLabel = content?.payment_label || content?.payment_method || '';
      totalsHtml = `<hr/>
        <div style="display:flex;justify-content:space-between"><span>Subtotal (excl. VAT)</span><span>${money(content?.subtotal)}</span></div>
        ${Number(content?.discount) > 0 ? `<div style="display:flex;justify-content:space-between"><span>Discount</span><span>-${money(content.discount)}</span></div>` : ''}
        <div style="display:flex;justify-content:space-between"><span>VAT${vatPct}</span><span>${money(content?.tax)}</span></div>
        <div style="display:flex;justify-content:space-between;font-weight:bold;font-size:14px;margin-top:2px"><span>TOTAL</span><span>${money(content?.total)}</span></div>
        ${payLabel ? `<div style="display:flex;justify-content:space-between;margin-top:3px"><span>Paid via</span><span>${esc(String(payLabel).toUpperCase())}</span></div>` : ''}`;
      sellerHtml = content?.zatca_seller_name
        ? `<div style="text-align:center;font-size:11px;font-weight:bold">${esc(content.zatca_seller_name)}</div>${content?.vat ? `<div style="text-align:center;font-size:10px">VAT: ${esc(content.vat)}</div>` : ''}`
        : '';
      metaHtml = `<div style="font-size:10px;color:#555;margin:2px 0">
        ${content?.order_number ? `<div>Order #${esc(content.order_number)}</div>` : ''}
        ${content?.order_type ? `<div>${esc(content.order_type)}</div>` : ''}
        ${content?.date ? `<div>${esc(new Date(content.date).toLocaleString())}</div>` : ''}
      </div>`;
    }

    // ZATCA QR — reuse the existing TLV builder + the qrcode dependency.
    let qrHtml = '';
    if (isReceipt) {
      try {
        const { buildZatcaQrPayload } = await import('@/lib/eposPrint');
        const payload = buildZatcaQrPayload(content);
        if (payload) {
          const QRCode = (await import('qrcode')).default;
          const dataUrl = await QRCode.toDataURL(payload, { margin: 1, width: 180 });
          qrHtml = `<div style="text-align:center;margin-top:6px"><img src="${dataUrl}" width="150" height="150" alt="ZATCA QR"/><div style="font-size:9px;color:#666">ZATCA e-invoice</div></div>`;
        }
      } catch { /* QR is best-effort — never block the print */ }
    }

    const html = `<!doctype html><html><head><style>
      @page { size: 80mm auto; margin: 2mm; }
      body { font-family: monospace; width: 76mm; padding: 4mm; font-size: 12px; }
      .t { text-align:center; font-weight:bold; margin-bottom:4px; }
      .hdr, .ftr { text-align:center; font-size:11px; margin:4px 0; white-space:pre-wrap; }
      hr { border:none; border-top:1px dashed #999; margin:4px 0; }
    </style></head><body>
      ${content?.header ? `<div class="hdr">${esc(content.header)}</div>` : ''}
      ${sellerHtml}
      <div class="t">${esc(title)}</div>
      ${metaHtml}
      <hr/>${rows}${totalsHtml}
      ${tailorHtml ? `<hr/>${tailorHtml}` : ''}
      ${qrHtml}
      ${content?.footer ? `<div class="ftr">${esc(content.footer)}</div>` : ''}
      <script>window.onload=()=>{setTimeout(()=>{window.print();},200)}</script>
    </body></html>`;
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
    setTimeout(() => { try { iframe.remove(); } catch { /* */ } }, 30000);
  }, [buildPrintPayload]);

  // Try to silently print via the bridge. Returns true on success (or
  // when the chosen `print_mode` deliberately handled it without the
  // bridge). For kitchen and bar roles items are pre-filtered by their
  // category's `print_to` so the kitchen only sees food and the bar
  // only sees drinks (iter605). iter611 — Each printer's `print_mode`
  // ("auto" | "silent" | "dialog") now drives the dispatch path.
  const printOrderViaBridge = useCallback(async (order, opts = {}) => {
    const role = opts.role || 'receipt';
    const openDrawer = role === 'receipt' && !!opts.openDrawer;
    try {
      const printer = pickPrinter(printers, role, user?.branch_id);
      if (!printer) return false;

      // iter845l — Android native app + USB/Bluetooth/Sunmi thermal printer.
      // MUST run before the ip_address gate, otherwise a USB-only printer
      // (no IP) silently returns false and falls back to window.print()
      // which opens Android's Default Print Service — which cannot see
      // thermal printers, so nothing ever prints.
      const isThermalCT = ['usb', 'bluetooth', 'sunmi'].includes(printer.connection_type);
      if (isThermalCT) {
        try {
          const { isNativeThermalAvailable, printNativeThermal } = await import('@/lib/nativeThermalPrint');
          if (isNativeThermalAvailable()) {
            // Apply per-category filtering for kitchen/bar tickets first.
            const filtered = (role === 'kitchen' || role === 'bar')
              ? filterOrderForPrinter(order, role, printer.station || '', categories, printers)
              : order;
            if ((role === 'kitchen' || role === 'bar') && (!filtered.items || filtered.items.length === 0)) {
              return true;
            }
            const content = buildPrintPayload(filtered, { role });
            const r = await printNativeThermal(printer, content, { openDrawer });
            if (r?.ok) {
              const roleLabel = role === 'kitchen' ? 'Kitchen ticket' : role === 'bar' ? 'Bar ticket' : 'Receipt';
              toast.success(`${roleLabel} printed → ${printer.name} (USB)`);
              return true;
            }
            toast.error(`Native USB print failed: ${r?.error || 'unknown'}. Open Settings → Printers → Help for the fix.`);
            return false;
          }
          // Not on Android native app (or plugin missing) → this printer
          // needs the Smarto app or Desktop app; surface a clear message
          // so the cashier isn't left wondering why nothing prints.
          // iter870 — distinguish "in Android app but plugin missing" vs
          // "in plain browser" so the fix is obvious. Also offer to route
          // via network (TCP 9100) when the printer already has an IP.
          if (!printer.ip_address) {
            const inAndroidApp = isCapacitorNative() && isAndroid();
            let msg;
            if (inAndroidApp) {
              // User IS in the Smarto Android app but the native plugin
              // isn't there — almost always because they installed an
              // older APK built before the SmartoPrinter bridge existed.
              msg = `${printer.name} (${printer.connection_type.toUpperCase()}) — the USB print bridge is missing from this APK. Update to the latest Smarto Android build, or add an IP address to switch this printer to network printing (TM-T88V has an Ethernet port). Tap here to open Printer Settings.`;
            } else {
              msg = `${printer.name} is a ${printer.connection_type.toUpperCase()} printer — open this page from the Smarto Android/Desktop app to print via USB, or add an IP address for network printing. Tap here to open Printer Settings.`;
            }
            toast.error(msg, {
              duration: 10000,
              action: {
                label: 'Fix',
                onClick: () => { try { window.location.assign('/settings/printers'); } catch { /* */ } },
              },
            });
            return false;
          }
        } catch (e) {
          toast.error(`USB print error: ${e?.message || e}. Try re-adding the printer.`);
          return false;
        }
      }

      if (!printer.ip_address) return false;
      // iter616 — Training mode forces every printer at the branch to
      // 'dialog' so new staff see exactly what would print before
      // paper moves. Branch-level override beats per-printer setting.
      const mode = isTrainingActive ? 'dialog' : (printer.print_mode || 'auto');
      // Apply per-category routing for prep tickets.
      const filtered = (role === 'kitchen' || role === 'bar')
        ? filterOrderForPrinter(order, role, printer.station || '', categories, printers)
        : order;
      if ((role === 'kitchen' || role === 'bar') && (!filtered.items || filtered.items.length === 0)) {
        return true;
      }
      // Dialog mode → always open the OS print dialog directly.
      if (mode === 'dialog') {
        openOrderInPrintDialog(filtered, role);
        toast.info(`Print mode: Dialog — opening OS print dialog for ${printer.name}.`);
        return true;
      }
      // iter675 — ePOS-Print Direct fast-path for ALL printer roles
      //           (receipt / kitchen / bar) on iPad + Samsung Tab. When
      //           the chosen printer has an IP, we POST the ePOS XML
      //           straight to it — no Bridge, no Cloud Relay, no noisy
      //           "No Bridge registered" 409 toast. Falls through to
      //           the existing Cloud Relay path if the printer has no
      //           IP (e.g., USB-only printers behind a Bridge).
      if (isLikelyMobileCashier() && printer?.ip_address) {
        const content = buildPrintPayload(filtered, { role });
        const r = await printViaEposDirect(printer, content, { openDrawer });
        if (r.ok) {
          const roleLabel = role === 'kitchen' ? 'Kitchen ticket' : role === 'bar' ? 'Bar ticket' : 'Receipt';
          toast.success(`${roleLabel} sent → ${printer.name} (ePOS Direct)`);
          return true;
        }
        // ePOS-Direct failed (usually cert not accepted on first run).
        // Surface a helpful hint, but DON'T spam the rose error toast on
        // the second-tier Cloud Relay attempt — fall through quietly.
        //
        // iter875 — Special-case DeviceNotFound: give the operator a
        // one-tap "Fix devid" button so they don't have to open Settings.
        // This mirrors the #1 recurring failure seen in production print
        // logs (log 2026-07-28: 4 back-to-back DeviceNotFound rejects).
        const isDevidBug =
          /DeviceNotFound/i.test(r.eposResult || '') ||
          /Wrong device id/i.test(r.msg || '');
        if (isDevidBug) {
          toast.error(`Printer rejected job — devid mismatch on ${printer.name}`, {
            duration: 10000,
            description: `Sent devid="${r.deviceId}" — Epson expects "local_printer".`,
            action: {
              label: 'Auto-fix',
              onClick: async () => {
                try {
                  await api.put(`/cashier/printers/${printer.id}`, {
                    ...printer,
                    epos_device_id: 'local_printer',
                  });
                  toast.success(`✓ ${printer.name} devid set to "local_printer". Re-tap Print.`);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Auto-fix failed. Open Settings → Printers.');
                }
              },
            },
          });
          return false;
        }
        toast.error(r.msg || `ePOS Direct failed for ${printer.name}. Open Settings → Printers and run the test.`);
        return false;
      }

      // iter648 — iPad / iOS path. The browser CAN'T reach localhost:9101
      // (no Python on iPad), so go straight to the Cloud Relay enqueue.
      // The Bridge running on a Mac/PC at the branch will pick it up
      // within seconds. We skip the bridgeAlive probe entirely on iOS.
      if (isLikelyIOS()) {
        const content = buildPrintPayload(filtered, { role });
        const r = await printViaCloud(api, {
          printerId: printer.id,
          branchId: user?.branch_id,
          payload: { ...content, ip: printer.ip_address, port: printer.port || 9100 },
        });
        if (r.ok) {
          const roleLabel = role === 'kitchen' ? 'Kitchen ticket' : role === 'bar' ? 'Bar ticket' : 'Receipt';
          toast.success(`${roleLabel} queued → ${printer.name} (Cloud Relay)`);
          return true;
        }
        if (r.status === 409) {
          // iter675 — When the device is a mobile cashier with no Bridge,
          // ePOS-Direct above should already have handled it. If we land
          // here it means the printer has no IP — log instead of toast
          // so it doesn't spam the cashier during a busy service.
          if (isLikelyMobileCashier()) {
            console.warn('[smarto][print] Cloud Relay 409 — no Bridge registered, and printer lacks IP for ePOS-Direct.', printer);
          } else {
            toast.error('No Bridge is registered. Run smarto_print_bridge.py on a PC/Mac at this branch (see /printer-setup → Step 5b).');
          }
        } else {
          toast.error(`Cloud Relay failed: ${r.error || 'unknown'}`);
        }
        return false;
      }
      const alive = await isBridgeAlive();
      if (alive) {
        const content = buildPrintPayload(filtered, { role });
        const r = await bridgePrint(printer, content, { kitchen: role !== 'receipt' });
        if (r.ok) {
          const roleLabel = role === 'kitchen' ? 'Kitchen ticket' : role === 'bar' ? 'Bar ticket' : 'Receipt';
          toast.success(`${roleLabel} printed → ${printer.name}`);
          return true;
        }
        toast.error(`Print failed: ${r.data?.error || 'unknown error'}`);
        return false;
      }
      // Bridge offline.
      if (mode === 'silent') {
        toast.error(`Silent mode requires the Print Bridge. Install it or switch ${printer.name} to "Auto" / "Dialog" mode.`);
        return false;
      }
      // Auto mode + no bridge → fall back to OS dialog.
      openOrderInPrintDialog(filtered, role);
      toast.info(`No bridge detected — opening OS print dialog for ${printer.name}.`);
      return true;
    } catch (_e) {
      return false;
    }
  }, [printers, user, categories, isTrainingActive, buildPrintPayload, openOrderInPrintDialog]);

  // iter606 — Fan out a prep ticket to EVERY kitchen / bar printer
  // configured for the current branch. Each printer receives only the
  // items its station's categories route to. Returns the number of
  // tickets actually fired.
  const printPrepTickets = useCallback(async (order) => {
    if (!order || !printers || !printers.length) return 0;
    window.dispatchEvent(new Event('pos:print-sent')); // lets PrintJobToaster confirm the printed status promptly
    // iter831 — Kitchen printing kill-switch. When the cashier has
    // toggled "Kitchen printing" OFF in POS settings (localStorage
    // kitchen_printing_enabled=0), skip prep tickets entirely — only
    // the customer receipt is produced.
    try {
      if (typeof window !== 'undefined' && localStorage.getItem('kitchen_printing_enabled') === '0') {
        return 0;
      }
    } catch (_e) { /* private mode */ }
    const branchId = user?.branch_id || null;
    const prepPrinters = printers.filter((p) =>
      (p.type === 'kitchen' || p.type === 'bar')
      && p.is_active !== false
      && (!p.branch_id || p.branch_id === branchId)
    );
    if (!prepPrinters.length) return 0;
    // iter611 — Bridge probed once; each printer's print_mode decides
    // whether it actually needs the bridge.
    // iter648 — iPad detection. When the cashier is on iOS we never
    // try the localhost probe (it always fails) and instead use the
    // Cloud Relay enqueue for every prep ticket.
    const useCloud = isLikelyIOS();
    const alive = useCloud ? false : await isBridgeAlive();
    let fired = 0;
    await Promise.all(prepPrinters.map(async (printer) => {
      const filtered = filterOrderForPrinter(order, printer.type, printer.station || '', categories, printers);
      if (!filtered.items || filtered.items.length === 0) return;
      const role = printer.type;
      const mode = isTrainingActive ? 'dialog' : (printer.print_mode || 'auto');
      const stationLabel = printer.station || (role === 'bar' ? 'Bar' : 'Kitchen');
      // Dialog mode → open the OS print dialog for this prep ticket.
      if (mode === 'dialog') {
        const dialogTicket = { ...filtered };
        openOrderInPrintDialog(dialogTicket, role);
        fired += 1;
        toast.info(`${stationLabel} ticket → OS print dialog`);
        return;
      }
      // iter648 — iPad path: enqueue via Cloud Relay.
      // iter675 — On mobile cashier with printer IP, prefer ePOS-Direct.
      if (useCloud) {
        if (isLikelyMobileCashier() && printer?.ip_address) {
          const content = buildPrintPayload(filtered, { role });
          const r = await printViaEposDirect(printer, content);
          if (r.ok) {
            fired += 1;
            toast.success(`${stationLabel} ticket → ${printer.name} (ePOS Direct)`);
            return;
          }
          toast.error(r.msg || `ePOS Direct failed for ${printer.name}.`);
          return;
        }
        const content = buildPrintPayload(filtered, { role });
        const r = await printViaCloud(api, {
          printerId: printer.id, branchId: user?.branch_id,
          payload: { ...content, ip: printer.ip_address, port: printer.port || 9100 },
        });
        if (r.ok) { fired += 1; toast.success(`${stationLabel} ticket queued (Cloud Relay)`); }
        else if (r.status === 409) toast.error('No Bridge registered. Add a printer IP and enable ePOS Direct.');
        else toast.error(`${printer.name}: relay failed`);
        return;
      }
      if (!alive) {
        if (mode === 'silent') {
          toast.error(`${printer.name}: silent mode needs the Print Bridge.`);
        } else {
          // Auto mode + no bridge → OS dialog fallback.
          openOrderInPrintDialog(filtered, role);
          fired += 1;
          toast.info(`${stationLabel} ticket → OS print dialog (bridge offline)`);
        }
        return;
      }
      const content = buildPrintPayload(filtered, { role });
      if (printer.station) {
        content.title = `${role === 'bar' ? 'BAR' : 'KITCHEN'} — ${printer.station}`;
      }
      const r = await bridgePrint(printer, content, { kitchen: true });
      if (r.ok) {
        fired += 1;
        toast.success(`${stationLabel} ticket → ${printer.name}`);
      } else {
        toast.error(`Print failed: ${printer.name}`);
      }
    }));
    return fired;
  }, [printers, user, categories, isTrainingActive, buildPrintPayload, openOrderInPrintDialog]);

  return { buildPrintPayload, openOrderInPrintDialog, printOrderViaBridge, printPrepTickets };
}

export default usePosPrinting;
